import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';

import SelectPlaceholder from '@/components/selectPlaceholder';
import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

export const RdsInstanceClassSelect = ({
  accountId,
  regionName,
  engine,
  engineVersion,
  rdsInstanceClass,
  isCluster,
  onChange,
  isOptionalField,
  hideLabel,
}: {
  accountId?: string;
  regionName?: string;
  engine?: string;
  engineVersion?: string;
  rdsInstanceClass?: string;
  isCluster: boolean;
  onChange: (rdsInstanceClass?: string) => void;
  isOptionalField?: boolean;
  hideLabel?: boolean;
}) => {
  const title = `DB instance class${isOptionalField ? ' (optional)' : ''}`;
  if (!accountId || !regionName || !engine || !engineVersion) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  return (
    <InnerRdsInstanceClassSelect
      accountId={accountId}
      regionName={regionName}
      engine={engine}
      engineVersion={engineVersion}
      onChange={onChange}
      rdsInstanceClass={rdsInstanceClass}
      isCluster={isCluster}
      title={title}
      hideLabel={hideLabel}
    />
  );
};

const InnerRdsInstanceClassSelect = ({
  accountId,
  regionName,
  engine,
  engineVersion,
  rdsInstanceClass,
  isCluster,
  onChange,
  title,
  hideLabel,
}: {
  accountId: string;
  regionName: string;
  engine: string;
  engineVersion: string;
  rdsInstanceClass?: string;
  isCluster: boolean;
  onChange: (rdsInstanceClass?: string) => void;
  title: string;
  hideLabel?: boolean;
}) => {
  const dal = useDAL();
  const { data: body } = dal.restore.rdsInstanceClasses.list(
    accountId,
    engine,
    engineVersion,
    regionName
  );

  if (!body?.instanceClasses.length) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  const defaultSelectText = 'Inherit from the snapshot';
  const clusterTooltip =
    'Each restored instance in the cluster can inherit its original DB instance class from the snapshot, or you can override with a different instance class. If you override, all restored instances will have the same DB instance class.';
  const singleTooltip =
    'The restored instance can inherit the original DB instance class from the snapshot, or you can override with a different instance class.';
  return (
    <FormControl size='small' className='flex-1'>
      {!hideLabel && (
        <FormLabel>
          {title}
          <Tooltip title={isCluster ? clusterTooltip : singleTooltip}>
            <Icon iconClass='material-symbols-info-outline' />
          </Tooltip>
        </FormLabel>
      )}
      <Select
        data-testid='rdsInstanceClassSelect'
        size='small'
        value={rdsInstanceClass || ''}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder title={defaultSelectText} />;
          }
          return value;
        }}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem key={'inherit-from-the-snapshot'} value={undefined}>
          {defaultSelectText}
        </MenuItem>
        {body.instanceClasses.map((value, idx) => (
          <MenuItem
            data-testid={`rdsInstanceClassOption-${idx}`}
            key={value}
            value={value}
          >
            {value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
